import L from 'leaflet';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import {
    GIS_POINT_LEGEND_ICON_SIZE_PIXELS,
    PointColors,
    COLORS_PROJECT
} from '../../../constants';
import { ScaleIcon } from '../icons/ScaleIcon';
import PointIcon from '../icons/PointIcon';

import '../../../leaflet/leaflet-ruler/leaflet-ruler';
import '../../../leaflet/leaflet-save/leaflet-save';
import '../../../leaflet/leaflet-zoomhome/leaflet-zoomhome';
import '@geoman-io/leaflet-geoman-free';
import 'leaflet-fullscreen';
import 'leaflet-html-legend';
import 'leaflet-mouse-position';

import '../../../leaflet/leaflet-ruler/leaflet-ruler.css';
import '../../../leaflet/leaflet-save/leaflet-save.css';
import '../../../leaflet/leaflet-zoomhome/leaflet-zoomhome.css';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import 'leaflet-html-legend/dist/L.Control.HtmlLegend.css';

import Swal from "sweetalert2"

const {color_shape_one,color_shape_two,color_shape_zero,color_shape_three}=COLORS_PROJECT

// Top-left

/**
 * @returns Ruler control.
 */
const getRulerControl = () => {
    return L.control.ruler({ position: 'topleft' });
};

/**
 * Adds Geoman control to map.
 *
 * @param {L.Map} map - Map.
 */
const addGeomanControl = (map) => {
    map.pm.addControls({
        position: 'topleft',
        drawCircle: false,
        drawCircleMarker: false,
    });

    const geomanControls = document.querySelector('.leaflet-pm-toolbar');
    if (geomanControls) {
        geomanControls.classList.add('custom-top-leaflet');
    }
};

/**
 * @param {L.Map} map - Map.
 * @param {(newGeojson: any) => any} openUserShapesModal - Function to open user shapes modal.
 * @returns Save control.
 */
const getSaveControl = (map, openUserShapesModal) => {
    const saveControl = L.control.save({
        onClick: () => {
            const fg = L.featureGroup();
            map.eachLayer((layer) => {
                if ((layer instanceof L.Path || layer instanceof L.Marker) && layer.pm) {
                    fg.addLayer(layer);
                }
            });
            console.log(JSON.stringify(fg.toGeoJSON(), null, 4));
            const geoValidation = fg.toGeoJSON()
            if(geoValidation.features.length !==0){
                openUserShapesModal(fg.toGeoJSON());
            }else{
                Swal.fire('Error','Debe dibujar al menos una figura','error')
            }
        },
    });

    // Agregar una clase CSS personalizada después de que el control se haya agregado al mapa
    map.on('controlsave:added', function () {
        const saveControls = document.querySelector('.leaflet-control-save');
        if (saveControls) {
            saveControls.classList.add('my-custom-class');
        }
    });

    return saveControl;
};

// Bottom-left

/**
 * @returns Scale control.
 */
const getScaleControl = () => {
    return L.control.scale();
};

/**
 * @returns Mouse position control.
 */
const getMousePositionControl = () => {
    return L.control.mousePosition();
};

// Top-right

/**
 * @param {L.LatLngBoundsExpression} defaultBounds - Map default bounds.
 * @returns Zoom home control.
 */
const getZoomHomeControl = (defaultBounds) => {
    return L.control.zoomHome({ defaultBounds });
};

/**
 * @returns Fullscreen control.
 */
const getFullscreenControl = () => {
    return new L.Control.Fullscreen({ position: 'topright' });
};

/**
 * @param {L.Control.LayersObject} baseLayers - Base layers (map tiles).
 * @param {L.Control.LayersObject} overlays - Overlays (layer groups, e.g., markers).
 * @returns Layers control.
 */
const getLayersControl = (baseLayers, overlays) => {
    return L.control.layers(baseLayers, overlays);
};

// Bottom-right

/**
 * @param {L.LayerGroup} pointsLayerGroup - Points layergroup.
 * @param {*} lightningsMarkerClusterGroup - Lightnings markerclustergroup.
 * @param {*} lineLightningsMarkerClusterGroup - LINET lightnings markerclustergroup.
 * @param {L.LayerGroup} announcersLayerGroup - Announcers layergroup.
 * @param {string} colorPoints - Colors for the points
 * @param {string} colorGLM - Colors for glm
 * @param {string} colorLinet - Colors for the linet
 * @param {L.LayerGroup} shapesLayerGroup - Shapes layergroup.
 * @returns Realtime legends control.
 */
const getLegendsControlRealtime = (
    pointsLayerGroup,
    ligmMarkerClusterGroup, 
    lightningsMarkerClusterGroup,
    lineLightningsMarkerClusterGroup, 
    announcersLayerGroup,
    colorPoints,
    colorGLM,
    colorLinet,
    shapesLayerGroup,
    nameLayerPoint,
) => {
    const legends = [];
    if(pointsLayerGroup){
        legends.push(getPointsLegend(pointsLayerGroup,colorPoints,nameLayerPoint));

    }
    if(shapesLayerGroup){
        legends.push(getShapesLayerGroup(shapesLayerGroup,colorPoints));
    }   
    if(ligmMarkerClusterGroup){
        legends.push(getLightningsLegend(ligmMarkerClusterGroup,colorGLM));
    }
    if (lightningsMarkerClusterGroup) {
        legends.push(getLightningsLegend(lightningsMarkerClusterGroup,colorGLM));
    }
    if (lineLightningsMarkerClusterGroup) {
        legends.push(getLightningsLegend(lineLightningsMarkerClusterGroup,colorLinet));
    }
    if (announcersLayerGroup) {
        legends.push(getAnnouncersLegend(announcersLayerGroup,colorPoints,nameLayerPoint));
    }

    return L.control.htmllegend({
        legends,
        position: 'bottomright',
    });
};

/**
 * @param {*} lightningsMarkerClusterGroup - Lightnings markerclustergroup.
 * @param {string} color - Colors for historic
 * @param {Array} legend Array of legend
 * @returns Historic legends control.
 */
const getLegendsControlHistoric = (lightningsMarkerClusterGroup,color,legend) => {
    return L.control.htmllegend({
        legends: [getLightningsLegendHistoric(lightningsMarkerClusterGroup,color,legend)],
        position: 'bottomright',
    });
};

/**
 * @param {L.LayerGroup} pointsLayerGroup - Points layerGroup.
 * @param {string} colorPoints - Colors for the points
 * @param {Array} nameLayerPoint - Name of layer
 * @returns Points legends.
 */
const getPointsLegend = (
    pointsLayerGroup,
    colorPoints,
    nameLayerPoint=[{
        nombrealertas: 'Sin riesgo (Nivel 0),Alerta (Nivel 1),Alerta (Nivel 2),Alerta (Nivel 3),',
    }]
    ) => {
    const arrColors = colorPoints.split(",")
    const {nombrealertas} = nameLayerPoint[0]
    const arrNames =nombrealertas.split(',')

    return {
        disableVisibilityControls: true,
        elements: [
            {
                html: ReactDOMServer.renderToString(
                    <PointIcon
                        fillColor={arrColors[3]}
                        fontSize={`${GIS_POINT_LEGEND_ICON_SIZE_PIXELS}px`}
                        strokeColor={PointColors[3].stroke}
                    />
                ),
                label: 'Alerta ' + arrNames[3] ? 'Alerta ' + arrNames[3] : 'Alerta (Nivel 3)',
            },
            {
                html: ReactDOMServer.renderToString(
                    <PointIcon
                        fillColor={arrColors[2]}
                        fontSize={`${GIS_POINT_LEGEND_ICON_SIZE_PIXELS}px`}
                        strokeColor={PointColors[2].stroke}
                    />
                ),
                label: 'Alerta ' + arrNames[2] ? 'Alerta ' + arrNames[2] : 'Alerta (Nivel 2)',
            },
            {
                html: ReactDOMServer.renderToString(
                    <PointIcon
                        fillColor={arrColors[1]}
                        fontSize={`${GIS_POINT_LEGEND_ICON_SIZE_PIXELS}px`}
                        strokeColor={PointColors[1].stroke}
                    />
                ),
                label: 'Alerta ' + arrNames[1] ? 'Alerta ' + arrNames[1] : 'Alerta (Nivel 1)',
            },
            {
                html: ReactDOMServer.renderToString(
                    <PointIcon
                        fillColor={arrColors[0]}
                        fontSize={`${GIS_POINT_LEGEND_ICON_SIZE_PIXELS}px`}
                        strokeColor={PointColors[0].stroke}
                    />
                ),
                label: 'Alerta ' + arrNames[0] ? 'Alerta ' + arrNames[0] : 'Sin riesgo (Nivel 0)',
            },
        ],
        layer: pointsLayerGroup,
        name: 'TWS',
    };
};

/**
 * @param {*} lightningsMarkerClusterGroup - Lightnings markerclustergroup.
 * @param {string} color - Colors for the points
 * @returns Lightnings legends.
 */
const getLightningsLegend = (lightningsMarkerClusterGroup,color) => {
    const colorSelector = color[0].split(',')
    return {
        disableVisibilityControls: true,
        elements: [
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[0]} />),
                label: '< 1 minuto',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[1]} />),
                label: '< 5 minutos',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[2]} />),
                label: '< 10 minutos',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[3]} />),
                label: '< 20 minutos',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[4]} />),
                label: '< 30 minutos',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[5]} />),
                label: '< 40 minutos',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[6]} />),
                label: '< 50 minutos',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[7]} />),
                label: '< 60 minutos',
            },
        ],
        layer: lightningsMarkerClusterGroup,
        name: 'Rayos',
    };
};

/**
 * @param {L.LayerGroup} announcersLayerGroup - Announcers layergroup.
 * @returns Announcers legends.
 */
const getAnnouncersLegend = (announcersLayerGroup, colorPoints,
    nameLayerPoint=[{
        nombrealertas: 'Sin riesgo (Nivel 0),Alerta (Nivel 1),Alerta (Nivel 2),Alerta (Nivel 3),',
    }]) => {
    const arrColors = colorPoints.split(",")
    const {nombrealertas} = nameLayerPoint[0]
    const arrNames =nombrealertas.split(',')
    return {
        disableVisibilityControls: true,
        elements: [
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={arrColors[3]} />),
                //label: 'Alerta (Nivel 3)',
                label: 'Alerta ' + arrNames[3] ? 'Alerta ' + arrNames[3] : 'Alerta (Nivel 3)',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={arrColors[2]} />),
                label: 'Alerta ' + arrNames[2] ? 'Alerta ' + arrNames[2] : 'Alerta (Nivel 2)',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={arrColors[1]} />),
                label: 'Alerta ' + arrNames[1] ? 'Alerta ' + arrNames[1] : 'Alerta (Nivel 1)',
            },
            {

                html: ReactDOMServer.renderToString(<ScaleIcon color={arrColors[0]} />),
                label: 'Operando',
            },
            {

                html: ReactDOMServer.renderToString(<ScaleIcon color= '#7d7a7c'  />),
                label: 'Desconectado',
            },
        ],
        layer: announcersLayerGroup,
        name: 'Dispositivos',
    };
};


/**
 * @param {*} lightningsMarkerClusterGroup - Lightnings markerclustergroup.
 * @returns Lightnings legends.
 */
 const getLightningsLegendHistoric = (lightningsMarkerClusterGroup,color,legends) => {
    const colorSelector = color[0].split(',')
    const c = colorSelector.reverse()
    let defaultLegend =[]
    if(!!legends){
        defaultLegend = legends.map( (legend,index) => {
            const{label}=legend
            return (
                {
                    html: ReactDOMServer.renderToString(<ScaleIcon color={c[index]} />),
                    label:label,
                }
            )
        })
    }else{
        defaultLegend =  [
            {
                //html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[0]} />),
                label: '',
            },
            /*{
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[1]} />),
                label: 'Grupo 2',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[2]} />),
                label: 'Grupo 3',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[3]} />),
                label: 'Grupo 4',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[4]} />),
                label: 'Grupo 5',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[5]} />),
                label: 'Grupo 6',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[6]} />),
                label: 'Grupo 7',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={colorSelector[7]} />),
                label: 'Grupo 8',
            },*/
        ]
    }
 

    return {
        disableVisibilityControls: true,
        elements: defaultLegend,
        layer: lightningsMarkerClusterGroup,
        name: 'Rayos',
    };
};


/**
 * @param {*} shapesLayerGroup - Shape shapesLayerGroup.
 * @returns Shape legends.
 */
 const getShapesLayerGroup = (shapesLayerGroup,colorPoints) => {
    const arrColors = colorPoints.split(",")
    
    return {
        disableVisibilityControls: true,
        elements: [
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={arrColors[3]} />),
                label: 'Alerta Alto Riesgo',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={arrColors[2]} />),
                label: 'Alerta Cercana',
            },
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={arrColors[1]} />),
                label: 'Alerta Aproximación',
            },            
            {
                html: ReactDOMServer.renderToString(<ScaleIcon color={color_shape_zero} />),
                label: 'Alerta Sin riesgo',
            },
        ],
        layer: shapesLayerGroup,
        name: 'Áreas',
    };
};
export {
    getRulerControl,
    addGeomanControl,
    getSaveControl,
    getMousePositionControl,
    getScaleControl,
    getZoomHomeControl,
    getFullscreenControl,
    getLayersControl,
    getLegendsControlHistoric,
    getLegendsControlRealtime,
};
